import type Luminary from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';

import { checkBlankScene } from './checks/blankScene';
import { checkCaptionOverlap } from './checks/captionOverlap';
import { checkDuplicateText } from './checks/duplicateText';
import { checkNoScenes } from './checks/noScenes';
import { checkOverlayCoverage } from './checks/overlayCoverage';
import { checkVoicedrivenSceneReadingTime } from './checks/voicedrivenSceneReadingTime';
import { checkVoicedrivenSceneTooLong } from './checks/voicedrivenSceneTooLong';
import { checkPlaceholderIcon } from './checks/placeholderIcon';
import { checkPlaceholderText } from './checks/placeholderText';
import { checkQuoteDesignWithoutAttribution } from './checks/quoteDesignWithoutAttribution';
import { checkMatchCutOpportunities } from './checks/matchCutOpportunities';
import { checkMusicTooShort } from './checks/musicTooShort';
import { checkSceneReadingTime } from './checks/sceneReadingTime';
import { checkTextClipping } from './checks/textClipping';
import { checkTextContrast } from './checks/textContrast';
import { checkTextOffscreen } from './checks/textOffscreen';
import { checkTextWidows } from './checks/textWidows';
import { checkVoiceGroupMissingTranscript } from './checks/voiceGroupMissingTranscript';

import { ErrorReporter } from 'js/src/libs/error-reporter';

export type LintSeverity = 'error' | 'warning';

export type LintCheckName =
    | 'no_scenes'
    | 'voice_group_missing_transcript'
    | 'overlay_coverage'
    | 'caption_overlap'
    | 'text_contrast'
    | 'text_clipping'
    | 'text_offscreen'
    | 'text_widows'
    | 'duplicate_text'
    | 'placeholder_text'
    | 'placeholder_icon'
    | 'quote_design_without_attribution'
    | 'match_cut_opportunities'
    | 'music_too_short'
    | 'scene_reading_time'
    | 'voicedriven_scene_reading_time'
    | 'voicedriven_scene_too_long'
    | 'blank_scene';

export interface LintFinding {
    check: LintCheckName;
    severity: LintSeverity;
    /**
     * The stable id of the scene the finding is about, or of every scene it spans. The lint
     * report shows the short form of this id so the agent can address the scene directly with the
     * same scene_id the edit tools (set_text/set_media/set_icon) take — never a positional index,
     * which drifts as scenes are added (AP-931). Scene-group-level findings use sceneGroupIndex
     * instead, since scene groups are still addressed by index.
     */
    sceneId?: string | string[];
    sceneGroupIndex?: number;
    message: string;
}

export interface LintReport {
    findings: LintFinding[];
    checksRun: LintCheckName[];
}

/**
 * A single lint check. Operates on one Luminary tree and returns the findings
 * it produced. Async because some checks (e.g. text contrast) render frames.
 */
export type LintCheck = (
    luminary: Luminary,
    getState: GetRootState,
) => LintFinding[] | Promise<LintFinding[]>;

const CHECKS: Record<LintCheckName, LintCheck> = {
    no_scenes: checkNoScenes,
    voice_group_missing_transcript: checkVoiceGroupMissingTranscript,
    overlay_coverage: checkOverlayCoverage,
    caption_overlap: checkCaptionOverlap,
    text_contrast: checkTextContrast,
    text_clipping: checkTextClipping,
    text_offscreen: checkTextOffscreen,
    text_widows: checkTextWidows,
    duplicate_text: checkDuplicateText,
    placeholder_text: checkPlaceholderText,
    placeholder_icon: checkPlaceholderIcon,
    quote_design_without_attribution: checkQuoteDesignWithoutAttribution,
    match_cut_opportunities: checkMatchCutOpportunities,
    music_too_short: checkMusicTooShort,
    scene_reading_time: checkSceneReadingTime,
    voicedriven_scene_reading_time: checkVoicedrivenSceneReadingTime,
    voicedriven_scene_too_long: checkVoicedrivenSceneTooLong,
    blank_scene: checkBlankScene,
};

export const ALL_LINT_CHECKS = Object.keys(CHECKS) as LintCheckName[];

/**
 * The severities each check can raise. Analytics lists a check only under these, so a warning-only
 * check doesn't report an `error_*` count that is always zero. Keep in step with the checks.
 */
export const LINT_CHECK_SEVERITIES: Record<LintCheckName, readonly LintSeverity[]> = {
    no_scenes: ['error'],
    voice_group_missing_transcript: ['error'],
    overlay_coverage: ['error'],
    caption_overlap: ['error'],
    text_contrast: ['error'],
    text_clipping: ['error'],
    text_offscreen: ['error'],
    text_widows: ['warning'],
    duplicate_text: ['warning'],
    placeholder_text: ['warning'],
    placeholder_icon: ['warning'],
    quote_design_without_attribution: ['warning'],
    match_cut_opportunities: ['warning'],
    music_too_short: ['error', 'warning'],
    scene_reading_time: ['warning'],
    voicedriven_scene_reading_time: ['warning'],
    voicedriven_scene_too_long: ['warning'],
    blank_scene: ['error'],
};

/**
 * Runs the configured battery of read-only quality checks against a single
 * Luminary tree and returns one combined report. Each check is isolated: a
 * check that throws is skipped rather than failing the whole run, so one
 * broken check never hides the findings of the others.
 */
export async function runVideoLint(
    luminary: Luminary,
    getState: GetRootState,
    options: { only?: LintCheckName[] } = {},
): Promise<LintReport> {
    const checksToRun = options.only?.length ? options.only : ALL_LINT_CHECKS;
    const findings: LintFinding[] = [];

    for (const checkName of checksToRun) {
        const check = CHECKS[checkName];
        if (!check) {
            continue;
        }
        try {
            const checkFindings = await check(luminary, getState);
            findings.push(...checkFindings);
        } catch (error) {
            ErrorReporter.capture(error, {
                level: 'warning',
                extra: { message: `Lint check "${checkName}" failed` },
            });
        }
    }

    return { findings, checksRun: checksToRun };
}
